import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LuSearch,
  LuSlidersHorizontal,
  LuLayoutGrid,
  LuList,
  LuHeart,
  LuChevronDown,
  LuChevronRight,
  LuBath,
  LuUtensils,
  LuMap,
} from "react-icons/lu";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import spacesService from "../services/spacesService";
import GoogleMap from "../components/GoogleMap";
import RequestViewingModal from "../components/RequestViewingModal";
import styles from "../assets/spacesAvailable.module.css";

const PRICE_OPTIONS = [
  { label: "Any price", value: "any", min: "no_min", max: "no_max" },
  { label: "£0 – £500", value: "0-500", min: 0, max: 500 },
  { label: "£500 – £1,000", value: "500-1000", min: 500, max: 1000 },
  { label: "£1,000 – £2,000", value: "1000-2000", min: 1000, max: 2000 },
  { label: "£2,000+", value: "2000+", min: 2000, max: "no_max" },
];

// We show every matching property on one page (no pagination) so the map
// displays all pins at once. The backend paginates via Array.slice with no
// hard cap, so a large per_page returns the full result set in one request.
const SHOW_ALL_PER_PAGE = 9999;

// "Newly listed" badge — only shown when the listing data actually supports it,
// so we never mislabel an older property.
const isNewlyListed = (space) => {
  const raw =
    space.created_at ||
    space.createdAt ||
    space.published_at ||
    space.publishedAt ||
    space.listed_at;
  if (!raw) return false;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return false;
  const days = (Date.now() - date.getTime()) / 86400000;
  return days >= 0 && days <= 30;
};

// Optional "Save X%" badge — rendered only when a discount value is present.
const getDiscountPercent = (space) =>
  space.discount_percentage ||
  space.savePercentage ||
  space.save_percent ||
  null;

const SpacesAvailable = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState(true);
  const [availableSpaces, setAvailableSpaces] = useState([]);
  const [availableSpacesInfo, setAvailableSpacesInfo] = useState({});
  const [totalItems, setTotalItems] = useState(0);
  const [searchParams, setSearchParams] = useState({
    location: "all",
    type: "all",
    price_min: "no_min",
    price_max: "no_max",
    availability: 1,
    available_for_couple: false,
  });

  // UI-only state for the redesigned search / filter / view controls
  const [locationInput, setLocationInput] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [savedIds, setSavedIds] = useState(() => new Set());
  const [priceDraft, setPriceDraft] = useState("any");
  const [spacesDraft, setSpacesDraft] = useState("any");
  const [kitchenDraft, setKitchenDraft] = useState("any");
  const [bathroomDraft, setBathroomDraft] = useState("any");

  // Request Viewing Modal state
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);

  const loadAvailableSpaces = useCallback(async () => {
    try {
      setLoading(true);
      console.log("🔍 [SPACES] Loading available spaces from live database...");

      // Use only live API - as per Laravel implementation.
      // Fetch every matching property in one request (no pagination).
      const response = await spacesService.getAvailableSpaces({
        page: 1,
        perPage: SHOW_ALL_PER_PAGE,
        ...searchParams,
      });

      console.log("✅ [SPACES] Live database data loaded successfully");

      setAvailableSpaces(response.data || []);
      setTotalItems(response.total || 0);
      setAvailableSpacesInfo(response.availableSpacesInfo || {});
    } catch (error) {
      console.error("❌ [SPACES] Error loading available spaces from database:", error);

      // Handle specific error cases (guardian status, permission)
      if (error.response?.status === 403) {
        const message = error.response?.data?.message || "You don't have permission to perform this action!";
        toast.error(message);
        console.error("❌ [SPACES] Permission/Status error:", message);
      } else if (error.response?.status === 401) {
        toast.error("Please login to view available spaces");
      } else {
        toast.error("Failed to load available spaces from database");
      }

      // Clear data on error - no mock data fallback as per Laravel implementation
      setAvailableSpaces([]);
      setAvailableSpacesInfo({});
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    loadAvailableSpaces();
  }, [loadAvailableSpaces]);

  const activeFilterCount = [
    priceDraft !== "any",
    spacesDraft !== "any",
    kitchenDraft !== "any",
    bathroomDraft !== "any",
  ].filter(Boolean).length;

  const applyFilters = () => {
    const price = PRICE_OPTIONS.find((p) => p.value === priceDraft) || PRICE_OPTIONS[0];
    setSearchParams((prev) => ({
      ...prev,
      location: locationInput.trim() || "all",
      price_min: price.min,
      price_max: price.max,
      // Minimum available spaces (vacant guardian rooms). The backend param is
      // named bedrooms_min, but it counts vacant rooms — i.e. the card's "spaces".
      bedrooms_min: spacesDraft !== "any" ? spacesDraft : undefined,
      // Minimum number of kitchens — backend filters on kitchen.
      kitchen: kitchenDraft !== "any" ? kitchenDraft : undefined,
      // Minimum number of bathrooms — backend filters on bathroom.
      bathroom: bathroomDraft !== "any" ? bathroomDraft : undefined,
    }));
  };

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter") {
      applyFilters();
    }
  };

  const toggleSaved = (propertyId) => {
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (next.has(propertyId)) {
        next.delete(propertyId);
      } else {
        next.add(propertyId);
      }
      return next;
    });
  };

  const handleRequestViewing = (propertyId) => {
    console.log("🔍 [SPACES] Request viewing clicked for property:", propertyId);

    // Find the property from availableSpaces to get its name
    const property = availableSpaces.find((space) => space.id === propertyId);

    setSelectedProperty({
      id: propertyId,
      name: property?.name || `Property ${propertyId}`,
      address: property?.address_1 || "",
      postcode: property?.postcode || "",
    });
    setShowRequestModal(true);
  };

  const handleKeepMeInformed = async (propertyId) => {
    try {
      // Call the backend API to keep me informed
      await spacesService.keepMeInformed(propertyId);

      // Update the local state to show "View Notifications" button
      setAvailableSpacesInfo((prev) => ({
        ...prev,
        [propertyId]: true,
      }));

      // Show success message matching Laravel implementation
      toast.success("You will be informed about new viewing!");

      console.log(`✅ [SPACES] Keep Me Informed successful for property ${propertyId}`);
    } catch (error) {
      console.error(`❌ [SPACES] Error in Keep Me Informed for property ${propertyId}:`, error);

      // Show error message
      if (error.response?.status === 403) {
        toast.error("You don't have permission to perform this action!");
      } else if (error.response?.data?.message?.includes("duplication")) {
        toast.error("You are already keeping informed about this property!");
      } else {
        toast.error("Failed to keep you informed. Please try again.");
      }
    }
  };

  const handleMoreInformation = (propertyId) => {
    // Navigate to property details page
    navigate(`/spaces/show/${propertyId}`);
  };

  const handleMapPropertyClick = (propertyId) => {
    // Handle property click from map marker
    handleMoreInformation(propertyId);
  };

  const handleViewNotifications = () => {
    // Navigate to the keep me informed notifications page
    navigate("/keep-me-informed");
    console.log(`📱 [SPACES] Navigating to Keep Me Informed notifications page`);
  };

  const handleCloseRequestModal = () => {
    setShowRequestModal(false);
    setSelectedProperty(null);
  };

  const handleViewingBooked = () => {
    console.log("🔄 [SPACES] Viewing booked, refreshing available spaces list");
    // Reload available spaces to update viewingsUpcoming counts
    loadAvailableSpaces();
  };

  // Conditional primary action per property, preserving the exact original logic:
  // upcoming viewings -> Request viewing; else keep-informed -> View Notifications /
  // Keep me Informed (permission gated).
  const renderConditionalAction = (space) => {
    if (space.viewingsUpcoming?.length > 0) {
      if (hasPermission("property_viewings.attend")) {
        return (
          <button
            className={`${styles.actionBtn} ${styles.secondaryBtn}`}
            onClick={() => handleRequestViewing(space.id)}
          >
            Request viewing
          </button>
        );
      }
      return null;
    }

    if (hasPermission("property_viewings.keep_informed")) {
      if (availableSpacesInfo[space.id]) {
        return (
          <button
            className={`${styles.actionBtn} ${styles.secondaryBtn}`}
            onClick={handleViewNotifications}
          >
            View Notifications
          </button>
        );
      }
      return (
        <button
          className={`${styles.actionBtn} ${styles.secondaryBtn}`}
          onClick={() => handleKeepMeInformed(space.id)}
        >
          Keep me Informed
        </button>
      );
    }

    return null;
  };

  const renderCard = (space) => {
    const price = space.price_from
      ? `£${space.price_from}`
      : "£--";
    const spacesCount = space.vacantRooms?.length || 0;
    const discount = getDiscountPercent(space);
    const isSaved = savedIds.has(space.id);

    return (
      <article key={space.id} className={styles.card}>
        <div className={styles.cardImageWrap}>
          <a href={`/spaces/show/${space.id}`}>
            <img
              src={space.main_photo || "/images/default-property.jpg"}
              alt={space.name || "Property"}
              className={styles.cardImage}
              loading="lazy"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/images/default-property.jpg";
              }}
            />
          </a>

          {isNewlyListed(space) && (
            <span className={styles.newBadge}>Newly listed</span>
          )}

          <button
            type="button"
            className={`${styles.wishBtn} ${isSaved ? styles.saved : ""}`}
            onClick={() => toggleSaved(space.id)}
            aria-label={isSaved ? "Remove from saved" : "Save property"}
            aria-pressed={isSaved}
          >
            <LuHeart fill={isSaved ? "currentColor" : "none"} />
          </button>
        </div>

        <div className={styles.cardBody}>
          <div className={styles.priceRow}>
            <span className={styles.price}>{price}</span>
            <span className={styles.priceUnit}>per month</span>
            {discount && <span className={styles.saveBadge}>Save {discount}%</span>}
          </div>

          <p className={styles.address}>
            {space.address_1}
            {space.town ? `, ${space.town}` : ""}
            <br />
            {space.postcode}
          </p>

          <div className={styles.chips}>
            <span className={styles.chip}>
              <LuBath className={styles.chipIcon} />
              {space.bathrooms ?? 0}
            </span>
            <span className={styles.chip}>
              <LuUtensils className={styles.chipIcon} />
              {space.kitchens ?? 0}
            </span>
            <span className={styles.chip}>
              <LuLayoutGrid className={styles.chipIcon} />
              {spacesCount} {spacesCount === 1 ? "space" : "spaces"}
            </span>
          </div>

          <div className={styles.cardActions}>
            {renderConditionalAction(space)}
            <button
              className={`${styles.actionBtn} ${styles.primaryBtn}`}
              onClick={() => handleMoreInformation(space.id)}
            >
              More Information
            </button>
          </div>
        </div>
      </article>
    );
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading available spaces...</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.layout}>
        {/* ----------------------------------------------- Results column */}
        <div className={styles.leftPanel}>
          {/* Listing header: search + filters + view toggle */}
          <div className={styles.listingHeader}>
            <div className={styles.headerRow}>
              <div className={styles.searchBox}>
                <span className={styles.searchIcon}>
                  <LuSearch />
                </span>
                <input
                  type="text"
                  className={styles.searchInput}
                  placeholder="Search by town, postcode or address..."
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                />
              </div>

              <div className={styles.headerControls}>
                <button
                  type="button"
                  className={`${styles.filterBtn} ${filtersOpen ? styles.active : ""}`}
                  onClick={() => setFiltersOpen((open) => !open)}
                >
                  <span className={styles.filterBtnIcon}>
                    <LuSlidersHorizontal />
                  </span>
                  Filters
                  {activeFilterCount > 0 && (
                    <span className={styles.filterCount}>{activeFilterCount}</span>
                  )}
                </button>

                <div className={styles.viewToggle}>
                  <button
                    type="button"
                    className={viewMode === "grid" ? styles.active : ""}
                    onClick={() => setViewMode("grid")}
                    aria-label="Grid view"
                    aria-pressed={viewMode === "grid"}
                  >
                    <LuLayoutGrid />
                  </button>
                  <button
                    type="button"
                    className={viewMode === "list" ? styles.active : ""}
                    onClick={() => setViewMode("list")}
                    aria-label="List view"
                    aria-pressed={viewMode === "list"}
                  >
                    <LuList />
                  </button>
                </div>
              </div>
            </div>

            {filtersOpen && (
              <div className={styles.filterRow}>
                <div className={styles.filterField}>
                  <span className={styles.filterLabel}>Price</span>
                  <div className={styles.filterControl}>
                    <select
                      className={styles.filterSelect}
                      value={priceDraft}
                      onChange={(e) => setPriceDraft(e.target.value)}
                    >
                      {PRICE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <LuChevronDown className={styles.chevron} />
                  </div>
                </div>

                <div className={styles.filterField}>
                  <span className={styles.filterLabel}>Spaces</span>
                  <div className={styles.filterControl}>
                    <select
                      className={styles.filterSelect}
                      value={spacesDraft}
                      onChange={(e) => setSpacesDraft(e.target.value)}
                    >
                      <option value="any">Any</option>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <option key={n} value={n}>
                          {String(n).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <LuChevronDown className={styles.chevron} />
                  </div>
                </div>

                <div className={styles.filterField}>
                  <span className={styles.filterLabel}>Kitchen</span>
                  <div className={styles.filterControl}>
                    <select
                      className={styles.filterSelect}
                      value={kitchenDraft}
                      onChange={(e) => setKitchenDraft(e.target.value)}
                    >
                      <option value="any">Any</option>
                      {[1, 2, 3].map((n) => (
                        <option key={n} value={n}>
                          {String(n).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <LuChevronDown className={styles.chevron} />
                  </div>
                </div>

                <div className={styles.filterField}>
                  <span className={styles.filterLabel}>Bathroom</span>
                  <div className={styles.filterControl}>
                    <select
                      className={styles.filterSelect}
                      value={bathroomDraft}
                      onChange={(e) => setBathroomDraft(e.target.value)}
                    >
                      <option value="any">Any</option>
                      {[1, 2, 3].map((n) => (
                        <option key={n} value={n}>
                          {String(n).padStart(2, "0")}
                        </option>
                      ))}
                    </select>
                    <LuChevronDown className={styles.chevron} />
                  </div>
                </div>

                <button type="button" className={styles.applyBtn} onClick={applyFilters}>
                  Apply
                </button>
              </div>
            )}
          </div>

          {/* Results count */}
          <div className={styles.subHeader}>{totalItems} Results</div>

          {/* Property cards */}
          {availableSpaces.length > 0 ? (
            <>
              <div
                className={`${styles.grid} ${viewMode === "list" ? styles.listView : ""}`}
              >
                {availableSpaces.map(renderCard)}
              </div>

              <div className={styles.pagination}>
                <div className={styles.paginationInfo}>
                  Showing all {totalItems}{" "}
                  {totalItems === 1 ? "property" : "properties"}
                </div>
              </div>
            </>
          ) : (
            <div className={styles.noResults}>
              Sorry, nothing matched your search criteria
            </div>
          )}
        </div>

        {/* --------------------------------------------------- Map column */}
        {!mapCollapsed && (
          <aside className={styles.mapPanel}>
            <div className={styles.mapSticky}>
              <div className={styles.mapInner}>
                <button
                  type="button"
                  className={styles.mapCollapseBtn}
                  onClick={() => setMapCollapsed(true)}
                  aria-label="Hide map"
                >
                  <LuChevronRight />
                </button>
                <GoogleMap
                  properties={availableSpaces}
                  onPropertyClick={handleMapPropertyClick}
                />
              </div>
            </div>
          </aside>
        )}
      </div>

      {mapCollapsed && (
        <button
          type="button"
          className={styles.mapShowBtn}
          onClick={() => setMapCollapsed(false)}
        >
          <LuMap /> Show map
        </button>
      )}

      {/* Request Viewing Modal */}
      <RequestViewingModal
        isOpen={showRequestModal}
        onClose={handleCloseRequestModal}
        propertyId={selectedProperty?.id}
        propertyName={selectedProperty?.name}
        onViewingBooked={handleViewingBooked}
      />
    </div>
  );
};

export default SpacesAvailable;
